"use client";

import {
  MadsBox,
  MadsButton,
  MadsMenuItem,
  MadsActionMenu,
} from "@mads/ui-react/next";
import Image from "next/image";
import { useHeader } from "@/components/layout/hooks/useHeader";
import dataQaIds from "@/constants/dataQaIds";
import { usePageContent } from "@/hooks/usePageContent";
import { useScreenType } from "@/hooks/useScreenType";
import { useProxyImage } from "@/hooks/useProxyImage";

interface HeaderProps {
  showSignOut: boolean;
  isPageDarkMode: boolean;
  isButtonsDark?: boolean;
}

export default function Header(props: Readonly<HeaderProps>) {
  const { showSignOut, isPageDarkMode, isButtonsDark } = props;

  const { languageCode, handleSignOut, handleLanguageChange, channel } =
    useHeader();

  const { pageContent } = usePageContent("corp-headers");
  const screenType = useScreenType();
  const isDesktop = screenType === "laptop";
  const logoUrl = useProxyImage(pageContent?.headerBonvoyLogo);
  if (!pageContent) return null;

  const qa = dataQaIds.header;
  const languages = [
    { label: pageContent.englishBtn, code: "en" },
    { label: pageContent.spanishBtn, code: "es" },
    { label: pageContent.chineseBtn, code: "zh" },
    { label: pageContent.frenchCanadianBtn, code: "fr-CA" },
  ];

  const logoSchemeClass = isPageDarkMode
    ? "mads-scheme-dark"
    : "mads-scheme-light";

  const desktopSchemeClass = isPageDarkMode
    ? "mads-scheme-light"
    : "mads-scheme-dark";

  let rootSchemeClass = logoSchemeClass;

  if (isButtonsDark && isDesktop) {
    rootSchemeClass = desktopSchemeClass;
  }

  return (
    <div className={rootSchemeClass}>
      <MadsBox
        position="absolute"
        top="0%"
        bg="bg-transparent"
        flex
        justify="space-between"
        px="layout-sm"
        align="center"
        w="100%"
        h="10vh"
        data-testid={qa.containerId}
        aria-label="Header Navigation"
      >
        {logoUrl && <Image
          style={{ filter: isPageDarkMode ? "none" : "invert(1)" }}
          src={logoUrl}
          alt={pageContent.headerBonvoyLogoAlt}
          height={40}
          width={160}
        />}



        <MadsBox flex gap="layout-xs" visibleFrom="md">
          {!channel && (
            <MadsActionMenu>
              <MadsButton
                slot="trigger"
                iconStart="website"
                neutral
                variant="tertiary"
              >
                {languages.find((lang) => lang.code === languageCode)?.label}
              </MadsButton>
              {languages.map((lang) => (
                <MadsMenuItem
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  {lang.label}
                </MadsMenuItem>
              ))}
            </MadsActionMenu>
          )}

          {showSignOut && !channel && (
            <MadsButton
              variant="secondary"
              iconStart="account"
              size="sm"
              onClick={handleSignOut}
            >
              {pageContent.signoutBtn || "Sign out"}
            </MadsButton>
          )}
        </MadsBox>

        {!channel && (
          <MadsBox hiddenFrom="md">
            <MadsActionMenu>
              <MadsButton
                slot="trigger"
                icon="menu-3-bars"
                neutral
                variant="tertiary"
                size="lg"
              />
              <MadsActionMenu>
                <MadsMenuItem slot="trigger" iconStart="website">
                  {languages.find((lang) => lang.code === languageCode)?.label}
                </MadsMenuItem>
                {languages.map((lang) => (
                  <MadsMenuItem
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                  >
                    {lang.label}
                  </MadsMenuItem>
                ))}
              </MadsActionMenu>
              {showSignOut && (
                <MadsMenuItem iconStart="account" onClick={handleSignOut}>
                  {pageContent.signoutBtn}
                </MadsMenuItem>
              )}
            </MadsActionMenu>
          </MadsBox>
        )}
      </MadsBox>
    </div>
  );
}
